import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  apiClient,
  ApiError,
  type PersonalSkillDetail,
  type SkillFile,
  type VariableVaultDiff,
} from '../lib/api';
import { pingCli, installViaCli, type CliStatus } from '../lib/cli-bridge';
import { AiPopover } from '../components/AiPopover';
import { PublishDialog } from '../components/PublishDialog';
import { FileTabs } from '../components/FileTabs';
import { AddFileDialog } from '../components/AddFileDialog';
import { RenameVariableDialog } from '../components/RenameVariableDialog';
import { AmbientAiBar } from '../components/AmbientAiBar';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type AiSurface = 'description' | 'body' | 'rewrite' | null;

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.round(hr / 24)}d`;
}

interface AiState {
  surface: AiSurface;
  busy: boolean;
  error: string | null;
  result: string | null;
}

const AI_TITLES: Record<Exclude<AiSurface, null>, string> = {
  description: 'description critic',
  body: 'body critic',
  rewrite: 'rewrite selection',
};

const SKILL_MD = 'SKILL.md';

interface FileBufferState {
  body: string;
  dirty: boolean;
}

export function SkillEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [skill, setSkill] = useState<PersonalSkillDetail | null>(null);
  const [files, setFiles] = useState<SkillFile[]>([]);
  const [activeTab, setActiveTab] = useState<string>(SKILL_MD);
  // file buffers: SKILL.md state + each rel_path state
  const [buffers, setBuffers] = useState<Record<string, FileBufferState>>({});
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [skillDirty, setSkillDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cli, setCli] = useState<CliStatus | null>(null);
  const [installMsg, setInstallMsg] = useState<string | null>(null);

  const [ai, setAi] = useState<AiState>({ surface: null, busy: false, error: null, result: null });
  const [showPublish, setShowPublish] = useState(false);
  const [publishMsg, setPublishMsg] = useState<string | null>(null);
  const [showAddFile, setShowAddFile] = useState(false);
  const [renameOldName, setRenameOldName] = useState<string | null>(null);
  const [vault, setVault] = useState<VariableVaultDiff | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const selectionRef = useRef<{ start: number; end: number } | null>(null);

  // Load skill + files
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const [s, fl] = await Promise.all([
          apiClient.skills.get(id),
          apiClient.files.list(id),
        ]);
        if (cancelled) return;
        setSkill(s);
        setFiles(fl.files);
        setName(s.name);
        setDescription(s.description ?? '');
        const next: Record<string, FileBufferState> = { [SKILL_MD]: { body: s.body, dirty: false } };
        for (const f of fl.files) next[f.rel_path] = { body: f.body, dirty: false };
        setBuffers(next);
        setSkillDirty(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : 'Failed to load skill');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Variable vault diff for this skill (refreshes when version_n changes)
  useEffect(() => {
    if (!id || !skill) return;
    let cancelled = false;
    apiClient.variables
      .vaultForSkill(id)
      .then((d) => {
        if (!cancelled) setVault(d);
      })
      .catch(() => {
        // best-effort; vault may not exist yet
      });
    return () => {
      cancelled = true;
    };
  }, [id, skill?.versionN]);

  // CLI status polling
  useEffect(() => {
    let cancelled = false;
    pingCli().then((status) => {
      if (!cancelled) setCli(status);
    });
    const t = setInterval(() => {
      pingCli().then((status) => {
        if (!cancelled) setCli(status);
      });
    }, 10000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const isActiveSkillMd = activeTab === SKILL_MD;
  const activeBody = buffers[activeTab]?.body ?? '';
  const dirtyPaths = new Set<string>();
  for (const [path, buf] of Object.entries(buffers)) {
    if (buf.dirty) dirtyPaths.add(path);
  }
  if (skillDirty) dirtyPaths.add(SKILL_MD);
  const dirty = dirtyPaths.size > 0;

  const setActiveBody = (next: string) => {
    setBuffers((prev) => ({
      ...prev,
      [activeTab]: { body: next, dirty: true },
    }));
  };

  const handleSave = useCallback(async () => {
    if (!id || !skill) return;
    setSaveStatus('saving');
    try {
      // Save the active tab. If SKILL.md, also save name/description if dirty.
      if (isActiveSkillMd) {
        const buf = buffers[SKILL_MD];
        const updated = await apiClient.skills.update(
          id,
          {
            name,
            description,
            body: buf?.body ?? skill.body,
          },
          { expectedContentHash: skill.contentHash },
        );
        setSkill(updated);
        setBuffers((prev) => ({ ...prev, [SKILL_MD]: { body: updated.body, dirty: false } }));
        setSkillDirty(false);
      } else {
        const buf = buffers[activeTab];
        if (!buf) return;
        const result = await apiClient.files.update(
          id,
          activeTab,
          { body: buf.body },
          skill.contentHash,
        );
        setSkill((prev) =>
          prev
            ? { ...prev, contentHash: result.content_hash, versionN: result.version_n }
            : prev,
        );
        setBuffers((prev) => ({ ...prev, [activeTab]: { body: buf.body, dirty: false } }));
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (err) {
      setSaveStatus('error');
      if (err instanceof ApiError && err.code === 'version_conflict') {
        setLoadError('This skill changed elsewhere. Reload to keep editing.');
      } else {
        console.error('save failed', err);
      }
    }
  }, [id, skill, isActiveSkillMd, activeTab, buffers, name, description]);

  // Cmd+S
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (dirty) void handleSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dirty, handleSave]);

  const handleAddFile = async (relPath: string) => {
    if (!id || !skill) return;
    const result = await apiClient.files.create(
      id,
      { rel_path: relPath, body: '' },
      skill.contentHash,
    );
    setSkill((prev) =>
      prev ? { ...prev, contentHash: result.content_hash, versionN: result.version_n } : prev,
    );
    const newFile: SkillFile = {
      rel_path: relPath,
      body: '',
      frontmatter: {},
      updated_at: new Date().toISOString(),
    };
    setFiles((prev) => [...prev, newFile].sort((a, b) => a.rel_path.localeCompare(b.rel_path)));
    setBuffers((prev) => ({ ...prev, [relPath]: { body: '', dirty: false } }));
    setActiveTab(relPath);
    setShowAddFile(false);
  };

  const handleDeleteActiveFile = async () => {
    if (!id || !skill || activeTab === SKILL_MD) return;
    if (!confirm(`Delete ${activeTab} from this skill?`)) return;
    const result = await apiClient.files.delete(id, activeTab, skill.contentHash);
    setSkill((prev) =>
      prev ? { ...prev, contentHash: result.content_hash, versionN: result.version_n } : prev,
    );
    setFiles((prev) => prev.filter((f) => f.rel_path !== activeTab));
    setBuffers((prev) => {
      const next = { ...prev };
      delete next[activeTab];
      return next;
    });
    setActiveTab(SKILL_MD);
  };

  const handleRenameVariable = async (newName: string) => {
    if (!id || !skill || !renameOldName) throw new Error('no skill');
    const result = await apiClient.files.renameVariable(
      id,
      { old_name: renameOldName, new_name: newName },
      skill.contentHash,
    );
    // Refresh skill + files
    const [s, fl] = await Promise.all([apiClient.skills.get(id), apiClient.files.list(id)]);
    setSkill(s);
    setFiles(fl.files);
    setName(s.name);
    setDescription(s.description ?? '');
    const next: Record<string, FileBufferState> = { [SKILL_MD]: { body: s.body, dirty: false } };
    for (const f of fl.files) next[f.rel_path] = { body: f.body, dirty: false };
    setBuffers(next);
    setSkillDirty(false);
    return { affected_files: result.affected_files, summary: result.summary };
  };

  const handleInstall = useCallback(async () => {
    if (!id) return;
    setInstallMsg('installing…');
    try {
      const result = await installViaCli(id);
      setInstallMsg(`installed → ${result.installedPaths.join(', ')}`);
    } catch (err) {
      setInstallMsg(err instanceof Error ? err.message : 'install failed');
    }
  }, [id]);

  const runAi = useCallback(
    async (surface: Exclude<AiSurface, null>, prompt: string, skillIdRef?: string) => {
      setAi({ surface, busy: true, error: null, result: null });
      try {
        const result = await apiClient.broker.call({
          role:
            surface === 'description'
              ? 'description-critic'
              : surface === 'body'
                ? 'body-critic'
                : 'inline-rewrite',
          prompt,
          skillId: skillIdRef,
        });
        setAi({ surface, busy: false, error: null, result: result.text });
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : 'AI call failed';
        setAi({ surface, busy: false, error: msg, result: null });
      }
    },
    [],
  );

  const handleCriticDescription = () => {
    if (!description) {
      setAi({
        surface: 'description',
        busy: false,
        error: 'Write a description first.',
        result: null,
      });
      return;
    }
    void runAi('description', description, id);
  };

  const handleCriticBody = () => {
    if (!activeBody) {
      setAi({ surface: 'body', busy: false, error: 'Body is empty.', result: null });
      return;
    }
    void runAi('body', activeBody, id);
  };

  const handleRewriteSelection = () => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (start === end) {
      setAi({
        surface: 'rewrite',
        busy: false,
        error: 'Select some text in the body first.',
        result: null,
      });
      return;
    }
    selectionRef.current = { start, end };
    void runAi('rewrite', activeBody.slice(start, end), id);
  };

  const acceptRewrite = (text: string) => {
    const sel = selectionRef.current;
    if (!sel) return;
    setActiveBody(activeBody.slice(0, sel.start) + text + activeBody.slice(sel.end));
    setAi({ surface: null, busy: false, error: null, result: null });
  };

  const acceptDescription = (text: string) => {
    setDescription(text.trim());
    setSkillDirty(true);
    setAi({ surface: null, busy: false, error: null, result: null });
  };

  // Detect variable references in active body (used for inspector "rename" affordance).
  const variableNames = Array.from(
    new Set(
      [...activeBody.matchAll(/\$\{([A-Z][A-Z0-9_]*)\}/g)].map((m) => m[1]),
    ),
  );

  if (loadError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-[var(--color-danger)]">
        {loadError} ·{' '}
        <Link to="/library" className="ml-2 underline">
          back to library
        </Link>
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-text-subtle">
        loading…
      </div>
    );
  }

  return (
    <div className="grid h-full grid-cols-1 bg-canvas md:grid-cols-[240px_1fr_300px]">
      <aside className="hairline-b md:hairline-b-0 md:hairline-r flex items-center gap-3 bg-surface p-3 md:block">
        <Link
          to="/library"
          className="text-[12px] text-text-muted hover:text-[var(--color-fg)]"
        >
          ← Library
        </Link>
        <div className="md:mt-4">
          <p className="text-[10px] uppercase tracking-wider text-text-subtle">Skill</p>
          <h2 className="mt-1 truncate font-mono text-[14px] text-text-muted">{skill.name}</h2>
        </div>
      </aside>

      <section className="flex min-h-[60vh] flex-col overflow-hidden md:min-h-0">
        <div className="hairline-b flex items-center justify-between bg-surface px-3">
          <div className="flex items-center">
            <button className="mode-tab active">Edit</button>
            <button className="mode-tab" disabled title="Diff mode lands when revisions ship">
              Diff
            </button>
            <button className="mode-tab" disabled title="Test mode lands in v1c">
              Test
            </button>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-text-subtle">
            <span className="font-mono">v{skill.versionN}</span>
            <span>·</span>
            <span className="font-mono">{relativeTime(skill.lastModifiedAt)} ago</span>
          </div>
        </div>

        <FileTabs
          active={activeTab}
          files={files}
          dirtyPaths={dirtyPaths}
          onSelect={setActiveTab}
          onAddFile={() => setShowAddFile(true)}
        />

        {isActiveSkillMd && (
          <div className="hairline-b grid grid-cols-[1fr_auto] gap-3 bg-canvas px-4 py-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-text-subtle">
                Name
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setSkillDirty(true);
                  }}
                  className="mt-1 block w-full bg-transparent font-mono text-[16px] outline-none"
                />
              </label>
              <div className="relative mt-2">
                <label className="block text-[10px] uppercase tracking-wider text-text-subtle">
                  Description
                  <textarea
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setSkillDirty(true);
                    }}
                    rows={2}
                    className="mt-1 block w-full resize-none bg-transparent text-[13px] outline-none"
                    placeholder="One sentence — what does this skill do?"
                  />
                </label>
                <button
                  type="button"
                  onClick={handleCriticDescription}
                  className="btn absolute right-0 top-0 text-[10px]"
                  title="Ask AI to tighten this description"
                >
                  AI critic
                </button>
                {ai.surface === 'description' && (
                  <AiPopover
                    title={AI_TITLES.description}
                    busy={ai.busy}
                    error={ai.error}
                    result={ai.result}
                    onAccept={acceptDescription}
                    onClose={() =>
                      setAi({ surface: null, busy: false, error: null, result: null })
                    }
                    onRetry={handleCriticDescription}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {!isActiveSkillMd && (
          <div className="hairline-b flex items-center justify-between bg-canvas px-4 py-2 text-[11px] text-text-subtle">
            <span>
              Editing <code className="font-mono">{activeTab}</code> · file scope · AI rewrites
              affect only this file.
            </span>
            <button
              type="button"
              onClick={handleDeleteActiveFile}
              className="btn text-[10px]"
              title="Remove this file from the skill"
            >
              Delete file
            </button>
          </div>
        )}

        <div className="relative flex-1 overflow-hidden bg-canvas">
          <div className="absolute right-3 top-2 z-10 flex gap-1">
            <button
              type="button"
              onClick={handleRewriteSelection}
              className="btn text-[10px]"
              title="Replace the selected text with an AI rewrite"
            >
              Rewrite selection
            </button>
            <button
              type="button"
              onClick={handleCriticBody}
              className="btn text-[10px]"
              title="Get advisory feedback (no replacement)"
            >
              Critique body
            </button>
            {(ai.surface === 'body' || ai.surface === 'rewrite') && (
              <AiPopover
                title={ai.surface ? AI_TITLES[ai.surface] : ''}
                busy={ai.busy}
                error={ai.error}
                result={ai.result}
                onAccept={ai.surface === 'rewrite' ? acceptRewrite : undefined}
                onClose={() => setAi({ surface: null, busy: false, error: null, result: null })}
                onRetry={ai.surface === 'body' ? handleCriticBody : handleRewriteSelection}
              />
            )}
          </div>
          <textarea
            ref={bodyRef}
            value={activeBody}
            onChange={(e) => setActiveBody(e.target.value)}
            spellCheck={false}
            className="h-full w-full resize-none bg-transparent p-4 font-mono text-[13px] leading-[20px] outline-none"
            placeholder={
              isActiveSkillMd
                ? '# Skill body in markdown.&#10;&#10;Lines starting with `## ` become section headings.&#10;`${VARIABLE_NAME}` placeholders are resolved at runtime.'
                : `# ${activeTab}`
            }
          />
        </div>

        <AmbientAiBar
          scope={isActiveSkillMd ? 'skill' : 'file'}
          skillId={skill.id}
          activeBody={activeBody}
          activePath={activeTab}
          onApplyToBody={(next) => setActiveBody(next)}
        />

        <div className="hairline-t flex items-center justify-between bg-surface px-3 py-1.5 text-[11px] text-text-subtle">
          <span>
            {activeBody.split('\n').length} lines · {activeBody.length.toLocaleString()} chars
            {dirty && ' · '}
            {dirty && (
              <span className="text-[var(--color-warning)]">
                unsaved on {[...dirtyPaths].join(', ')}
              </span>
            )}
          </span>
          <span>v{skill.versionN}</span>
        </div>
      </section>

      <aside className="hairline-t md:hairline-t-0 md:hairline-l flex flex-col bg-surface text-[12px]">
        <div className="hairline-b flex items-center justify-between px-3 py-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            Inspector
          </span>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saveStatus === 'saving'}
            className={dirty ? 'btn btn-primary' : 'btn'}
          >
            {saveStatus === 'saving'
              ? 'Saving…'
              : dirty
                ? `Save (⌘S)`
                : 'Saved'}
          </button>
        </div>

        <dl className="space-y-3 px-3 py-3">
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-text-subtle">
              Last modified
            </dt>
            <dd className="mt-0.5">{new Date(skill.lastModifiedAt).toLocaleString()}</dd>
          </div>
          {skill.importedFrom && (
            <div>
              <dt className="text-[10px] uppercase tracking-wider text-text-subtle">
                Imported from
              </dt>
              <dd className="mt-0.5 truncate">
                <a
                  href={skill.importedFrom}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--color-accent)] hover:underline"
                  title={skill.importedFrom}
                >
                  {skill.importedFrom.replace(/^https?:\/\//, '').slice(0, 42)}
                  {skill.importedFrom.length > 50 ? '…' : ''}
                </a>
              </dd>
            </div>
          )}
        </dl>

        {isActiveSkillMd && variableNames.length > 0 && (
          <div className="hairline-t px-3 py-3">
            <p className="text-[10px] uppercase tracking-wider text-text-muted">Variables in scope</p>
            <ul className="mt-2 space-y-1.5">
              {variableNames.map((v) => {
                const isMissing = vault?.missing.includes(v);
                return (
                  <li
                    key={v}
                    className="flex items-center justify-between font-mono text-[11px]"
                  >
                    <span className="flex items-center gap-1.5">
                      <span
                        title={isMissing ? 'Not in your vault — install will fail' : 'In vault'}
                        className="h-[6px] w-[6px] rounded-full"
                        style={{
                          background: isMissing
                            ? 'var(--color-warning)'
                            : 'var(--color-success)',
                        }}
                      />
                      <span>${'{'}{v}{'}'}</span>
                    </span>
                    <span className="flex gap-2">
                      {isMissing && (
                        <Link
                          to="/settings/variables"
                          className="text-[10px] text-[var(--color-warning)] hover:underline"
                        >
                          set →
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={() => setRenameOldName(v)}
                        className="text-[10px] text-text-subtle hover:text-[var(--color-accent)]"
                      >
                        rename →
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-2 text-[10px] text-text-subtle">
              Rename cascades atomically across SKILL.md + every reference file.
              {vault && vault.missing.length > 0 && (
                <>
                  {' '}
                  <span className="text-[var(--color-warning)]">
                    {vault.missing.length} missing — fill in{' '}
                    <Link to="/settings/variables" className="underline">
                      /settings/variables
                    </Link>
                  </span>
                </>
              )}
            </p>
          </div>
        )}

        <div className="hairline-t px-3 py-3">
          <p className="text-[10px] uppercase tracking-wider text-text-muted">Share</p>
          {skill.lastPublishedToUrl && (
            <p className="mt-1 truncate text-[11px] text-text-subtle">
              Published →{' '}
              <a
                href={skill.lastPublishedToUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[var(--color-accent)] hover:underline"
              >
                gist
              </a>
            </p>
          )}
          <button
            type="button"
            onClick={() => setShowPublish(true)}
            className="btn mt-2 w-full justify-center"
          >
            Publish to GitHub
          </button>
          {publishMsg && (
            <p className="mt-2 truncate text-[11px] text-text-subtle">{publishMsg}</p>
          )}
        </div>

        <div className="hairline-t px-3 py-3">
          <p className="text-[10px] uppercase tracking-wider text-text-muted">CLI</p>
          {cli ? (
            <>
              <p className="mt-1 flex items-center gap-2 text-[11px]">
                <span
                  className="h-[6px] w-[6px] rounded-full"
                  style={{ background: 'var(--color-success)' }}
                />
                <span className="font-mono">v{cli.version}</span>
                <span className="text-text-subtle">·</span>
                <span className="truncate text-text-subtle">{cli.user ?? 'no user'}</span>
              </p>
              <button
                type="button"
                onClick={handleInstall}
                className="btn mt-2 w-full justify-center"
              >
                Install to Claude Code
              </button>
              {installMsg && (
                <p className="mt-2 break-words text-[11px] text-text-subtle">{installMsg}</p>
              )}
            </>
          ) : (
            <p className="mt-1 text-[11px] text-text-subtle">
              CLI not running.{' '}
              <button
                onClick={() => navigate('/settings/cli')}
                className="text-[var(--color-accent)] hover:underline"
              >
                Install instructions →
              </button>
            </p>
          )}
        </div>
      </aside>

      {showPublish && (
        <PublishDialog
          skillId={skill.id}
          onClose={() => setShowPublish(false)}
          onPublished={(result) => {
            setShowPublish(false);
            setPublishMsg(`published → ${result.url}`);
            setSkill((prev) =>
              prev
                ? {
                    ...prev,
                    lastPublishedToUrl: result.url,
                    lastPublishedToSha: result.sha,
                    lastPublishedAt: result.publishedAt,
                  }
                : prev,
            );
          }}
        />
      )}

      {showAddFile && (
        <AddFileDialog onClose={() => setShowAddFile(false)} onCreate={handleAddFile} />
      )}

      {renameOldName && (
        <RenameVariableDialog
          oldName={renameOldName}
          onClose={() => setRenameOldName(null)}
          onRename={handleRenameVariable}
        />
      )}
    </div>
  );
}
